import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import type {
  AdvertisementNamespaceSubscription,
  SubscriptionAdvertisement,
} from '../../@types/AdvertismentSubscription';
import type { ElasticVehicle } from '../../@types/ElasticVehicle';
import type { Retailer } from '../../@types/Retailer';
import type Item from '../../@types/item';
import { create } from '../../actions/ad-subscription';
import { all } from '../../actions/ad-subscription-namespaces';
import { trackSoftConversionEvent } from '../../actions/tracking';
import { images, parsers } from '../../helpers';
import { ExchangeTypes, useDataExchangeStatus } from '../../hooks/exchange-status';
import type { RootState } from '../../reducers';
import { softConversionEvents } from '../../shared/tracking';
import { ActionList, ActionListItem } from '../../styled-components/components/ActionList';
import Alert from '../../styled-components/components/Alert';
import { Button } from '../../styled-components/components/Button';
import Modal from '../../styled-components/components/Modal';
import { SeparatorLabel } from '../../styled-components/components/SeparatorLabel';
import { StackMedium } from '../../styled-components/components/Stack';
import { Text } from '../../styled-components/components/Text';
import { getRetailerDocument, getSearchDocument } from '../../utils/reselectors';
import SubscriptionNamespaceForm from './ad-subscription-namespace-form';

export const getContactOptions = (item: Item | ElasticVehicle, retailer: Retailer | null) => {
  const c1 = item?.contactOptions;
  const c2 = retailer?.defaultContactOptions;

  return {
    avatarUrl: c1?.avatarUrl || c2?.avatarUrl,
    displayName: c1?.displayName || c2?.displayName,
    id: c1?.id || c2?.id,
    telephone: parsers.parsePhoneNumber(
      c1?.telephone ||
        c2?.telephone ||
        (retailer && retailer.telephoneNumber ? retailer.telephoneNumber : null),
    ),
    title: c1?.title || c2?.title,
    email: c1?.email || c2?.email,
  };
};

const defaultImageFormat = '770x514';

const getPreviewImage = (item: Item | ElasticVehicle) => {
  if (item.media && item.media.length && !!item.media.filter((x) => x.type === 'Image').length) {
    return item.media
      ?.filter((x) => x.type === 'Image')
      ?.sort((a, b) => a.sortOrder - b.sortOrder)[0]
      ?.files?.[0]?.formats?.find((f) => f.format === defaultImageFormat)?.url;
  }

  if (item.featuredImage) {
    return images.forItem(item, defaultImageFormat);
  }

  return null;
};

const toSubscriptionRequestBody = (
  item: Item,
  retailer: Retailer | null,
  namespaceId: string,
): SubscriptionAdvertisement => ({
  name: item.title,
  namespaceId: namespaceId || undefined,
  advertisementId: item.id,
  title: item.title,
  shortDescription: item.shortDescription || '',
  image: getPreviewImage(item) || '',
  mileage: item.mileage,
  price: item.price,
  leasingPrice: item.leasingPrice,
  manufactureYear: item.manufactureYear,
  modelYear: item.modelYear,
  fuelType: item.fuelType,
  gearboxType: item.gearboxType,
  latitude: (item.position?.location.lat || item.branches?.[0]?.latitude) ?? 0,
  longitude: (item.position?.location.lon || item.branches?.[0]?.longitude) ?? 0,
  city: item.position?.city || item?.branches?.[0].city || ',',
  createdAt: item.itemCreated,
  updatedAt: new Date().toISOString(),
  lastNotifiedAt: new Date().toISOString(),
  sellerName: getContactOptions(item, retailer)?.displayName || '',
  id: item.id,
  branchName: item.branches?.[0]?.name || retailer?.name || '',
  isActive: true,
});

const searchItemToSubscriptionRequestBody = (
  item: Item | ElasticVehicle,
  namespaceId: string,
): SubscriptionAdvertisement => ({
  name: item.title,
  namespaceId: namespaceId || undefined,
  advertisementId: item.id,
  title: item.title,
  shortDescription: item.shortDescription || '',
  image: getPreviewImage(item) || '',
  mileage: item.mileage,
  price: item.price,
  leasingPrice: item.leasingPrice,
  manufactureYear: item.manufactureYear,
  modelYear: item.modelYear,
  fuelType: item.fuelType,
  gearboxType: item.gearboxType,
  sellerName: getContactOptions(item, null)?.displayName || '',
  branchName: item.branches?.[0]?.name || '',
  latitude: (item.position?.location.lat || item.branches?.[0]?.latitude) ?? 0,
  longitude: (item.position?.location.lon || item.branches?.[0]?.longitude) ?? 0,
  city: item.position?.city || item?.branches?.[0].city || ',',
  createdAt: item.itemCreated,
  id: item.id,
  isActive: true,
  updatedAt: new Date().toISOString(),
  lastNotifiedAt: new Date().toISOString(),
});

interface AdSubscriptionCreateModalProps {
  searchItem?: Item | ElasticVehicle;
  onClose: () => void;
}

const AdSubscriptionCreateModal = ({ searchItem, onClose }: AdSubscriptionCreateModalProps) => {
  const { t } = useTranslation(['common', 'list', 'search']);
  const dispatch = useDispatch();
  const item = useSelector(getSearchDocument);
  const retailer = useSelector(getRetailerDocument);
  const namespaces = useSelector<RootState, AdvertisementNamespaceSubscription[]>(
    (s) => s.subscriptions.namespaces.list,
  );
  const requestStatus = useSelector<RootState, number>(
    (s) => s.subscriptions.advertisement.requestStatus,
  );
  const responseStatus = useSelector<RootState, number>(
    (s) => s.subscriptions.advertisement.responseStatus,
  );
  const actions = bindActionCreators(
    {
      load: all,
      create,
      track: trackSoftConversionEvent,
    },
    dispatch,
  );

  const { pending: isFetching } = useDataExchangeStatus(ExchangeTypes.Fetching, [
    requestStatus,
    responseStatus,
  ]);
  const { pending: isCreating, successful: wasCreated } = useDataExchangeStatus(
    ExchangeTypes.Creating,
    [requestStatus, responseStatus],
  );

  useEffect(() => {
    if (!namespaces.length) actions.load();
  }, []);

  useEffect(() => {
    if (wasCreated) onClose();
  }, [wasCreated]);

  const onCreate = (namespaceId: string) => {
    if (searchItem) {
      actions.create(searchItemToSubscriptionRequestBody(searchItem, namespaceId));
    } else if (item) {
      actions.create(toSubscriptionRequestBody(item, retailer, namespaceId));
    }

    if (searchItem || item) {
      actions.track(searchItem || item, softConversionEvents.SOFT_CONVERSIONS_SAVE);
    }

    /*
    if (item) {
      actions.create(toSubscriptionRequestBody(item, retailer, namespaceId));
    }

    if (item) {
      actions.track(item, softConversionEvents.SOFT_CONVERSIONS_SAVE);
    }
      */
  };

  return (
    <Modal heading={t('Save ad', { ns: 'search' })} onClose={onClose}>
      <StackMedium>
        <Alert tone="info">{t('createListHelp', { ns: 'list' })}</Alert>
      </StackMedium>
      {!isFetching && !isCreating && namespaces.length ? (
        <>
          <StackMedium>
            <SeparatorLabel>{t('addToExistingList', { ns: 'list' })}</SeparatorLabel>
          </StackMedium>
          <StackMedium>
            <ActionList>
              {namespaces.map((namespace) => (
                <ActionListItem key={namespace.id}>
                  <Text $weight="bold">{namespace.name}</Text>
                  <Button
                    onClick={() => onCreate(namespace.id)}
                    title={t('add', { ns: 'common' })}
                    $tone="secondary"
                    $size="small"
                  >
                    {t('add', { ns: 'common' })}
                  </Button>
                </ActionListItem>
              ))}
            </ActionList>
          </StackMedium>
          <StackMedium>
            <SeparatorLabel>{t('createNewList', { ns: 'list' })}</SeparatorLabel>
          </StackMedium>
        </>
      ) : null}
      <StackMedium>
        <SubscriptionNamespaceForm />
      </StackMedium>
    </Modal>
  );
};

export default AdSubscriptionCreateModal;
